import React from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import LegalNotices from "./pages/LegalNotices";
import Terms from "./pages/Terms";
import LegalCenter from "./pages/LegalCenter";
import CommunityRules from "./pages/CommunityRules";
import PaymentsPolicy from "./pages/PaymentsPolicy";
import Support from "./pages/Support";
import HelpGuide from "./pages/HelpGuide";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Explore from "./pages/Explore";
import Messages from "./pages/Messages";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import VerifyIdentity from "./pages/VerifyIdentity";
import Account from "./pages/Account";
import Notifications from "./pages/Notifications";
import Rating from "./pages/Rating";
import Garden from "./pages/Garden";
import GardenCategory from "./pages/GardenCategory";
import GardenDesires from "./pages/GardenDesires";
import NotreJardin from "./pages/NotreJardin";
import ProfileVisitor from "./pages/ProfileVisitor";
import Venue from "./pages/Venue";
import Create from "./pages/Create";
import Community from "./pages/Community";
import Ritual from "./pages/Ritual";
import BirthdayEvents from "./pages/BirthdayEvents";
import BirthdayEventCreate from "./pages/BirthdayEventCreate";
import BirthdayEventDetail from "./pages/BirthdayEventDetail";
import MyAddresses from "./pages/MyAddresses";
import Publication from "./pages/Publication";
import Search from "./pages/Search";
import Bookings from "./pages/Bookings";
import PowerRequests from "./pages/PowerRequests";
import Move from "./pages/Move";
import ProEventsNetwork from "./pages/ProEventsNetwork";
import SponsorSuccess from "./pages/SponsorSuccess";
import MySponsorships from "./pages/MySponsorships";
import MyVenues from "./pages/MyVenues";
import MyLives from "./pages/MyLives";
import AdTool from "./pages/AdTool";
import Premium from "./pages/Premium";
import FriendsMap from "./pages/FriendsMap";
import VenueProviders from "./pages/VenueProviders";
import VenueAgenda from "./pages/VenueAgenda";
import Admin from "./pages/Admin";
import Join from "./pages/Join";
import Pro from "./pages/Pro";
import WatchRoom from "./pages/WatchRoom";
import Invite from "./pages/Invite";
import InviteContacts from "./pages/InviteContacts";
import MandatoryAvatarStep from "./components/MandatoryAvatarStep";
import { CallProvider } from "./context/CallContext";
import CallOverlay from "./components/CallOverlay";
import VerificationGateModal from "./components/VerificationGateModal";
import AvatarRejectedModal from "./components/AvatarRejectedModal";
import CookieConsentBanner from "./components/CookieConsentBanner";

// Un visiteur anonyme qui clique "Revendiquer ce lieu" (fiche publique SEO) ou "Créer ma page
// gratuitement" (page /pro) atterrit ici avant de se connecter — sans ça, il perdrait son intention
// initiale et retomberait sur l'accueil générique après connexion. Login.tsx/Register.tsx relisent
// ce même paramètre `redirect` pour y renvoyer une fois authentifié.
function RedirectToLogin() {
  const location = useLocation();
  const target = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
  return <Navigate to={target} replace />;
}

function AppRoutes() {
  const { token, user } = useApp();
  // Nouveau compte (avatar_required, voir migrate.ts) : rien d'autre ne doit être atteignable
  // tant que la photo n'est pas posée — couvre aussi les inscriptions Google/Apple, qui
  // contournent Register.tsx et son propre appel à /auth/register.
  const mustSetAvatar = !!token && user?.avatar_required === true;

  return (
    <Routes>
      {!token ? (
        <>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/join" element={<Join />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/legal" element={<LegalCenter />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/legal-notices" element={<LegalNotices />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/community-rules" element={<CommunityRules />} />
          <Route path="/payments-policy" element={<PaymentsPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/help" element={<HelpGuide />} />
          <Route path="/pro" element={<Pro />} />
          <Route path="*" element={<RedirectToLogin />} />
        </>
      ) : mustSetAvatar ? (
        <Route path="*" element={<MandatoryAvatarStep />} />
      ) : (
        <Route path="*" element={
          <CallProvider>
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/messages/:conversationId" element={<Messages />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/verify-identity" element={<VerifyIdentity />} />
                <Route path="/account" element={<Account />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/rating" element={<Rating />} />
                <Route path="/garden" element={<Garden />} />
                <Route path="/garden/desires" element={<GardenDesires />} />
                <Route path="/garden/:category" element={<GardenCategory />} />
                <Route path="/notre-jardin" element={<NotreJardin />} />
                <Route path="/profile/:id" element={<ProfileVisitor />} />
                <Route path="/venue/:id" element={<Venue />} />
                <Route path="/venue/:id/prestataires" element={<VenueProviders />} />
                <Route path="/venue-agenda/:id" element={<VenueAgenda />} />
                <Route path="/create" element={<Create />} />
                <Route path="/community/:id" element={<Community />} />
                <Route path="/ritual/:id" element={<Ritual />} />
                <Route path="/birthday-events" element={<BirthdayEvents />} />
                <Route path="/birthday-events/create" element={<BirthdayEventCreate />} />
                <Route path="/birthday-events/:id" element={<BirthdayEventDetail />} />
                <Route path="/my-addresses" element={<MyAddresses />} />
                <Route path="/publication/:id" element={<Publication />} />
                <Route path="/search" element={<Search />} />
                <Route path="/bookings" element={<Bookings />} />
                <Route path="/power-requests" element={<PowerRequests />} />
                <Route path="/move" element={<Move />} />
                <Route path="/pro-events-network" element={<ProEventsNetwork />} />
                <Route path="/sponsor/success" element={<SponsorSuccess />} />
                <Route path="/my-sponsorships" element={<MySponsorships />} />
                <Route path="/my-venues" element={<MyVenues />} />
                <Route path="/my-lives" element={<MyLives />} />
                <Route path="/ad-tool" element={<AdTool />} />
                <Route path="/premium" element={<Premium />} />
                <Route path="/friends-map" element={<FriendsMap />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/invite" element={<Invite />} />
                <Route path="/invite-contacts" element={<InviteContacts />} />
                <Route path="/join" element={<Join />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/legal" element={<LegalCenter />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/legal-notices" element={<LegalNotices />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/community-rules" element={<CommunityRules />} />
                <Route path="/payments-policy" element={<PaymentsPolicy />} />
                <Route path="/support" element={<Support />} />
                <Route path="/help" element={<HelpGuide />} />
                <Route path="/pro" element={<Pro />} />
                <Route path="/watch/:id" element={<WatchRoom />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
            <CallOverlay />
            <VerificationGateModal />
            <AvatarRejectedModal />
          </CallProvider>
        } />
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <BrowserRouter>
          <AppRoutes />
          <CookieConsentBanner />
        </BrowserRouter>
      </AppProvider>
    </HelmetProvider>
  );
}
